import { createContext, useContext, useReducer, useEffect, useMemo } from 'react';

const LayoutContext = createContext(null);
const LayoutDispatchContext = createContext(null);

const actionTypes = {
  INIT: 'INIT',
  SET_SUBHEADER: 'SET_SUBHEADER',
  SHOW_SPLASH_SCREEN: 'SHOW_SPLASH_SCREEN',
  HIDE_SPLASH_SCREEN: 'HIDE_SPLASH_SCREEN'
};

function reducer(state, { type, payload }) {
  switch (type) {
    case actionTypes.INIT:
      return { ...state, subheader: payload.subheader };
    
    case actionTypes.SET_SUBHEADER:
      return { ...state, subheader: payload };
    
    case actionTypes.SHOW_SPLASH_SCREEN:
      return {
        ...state,
        splashScreen: {
          ...state.splashScreen,
          refs: { ...state.splashScreen.refs, [payload.id]: true }
        }
      };
    
    case actionTypes.HIDE_SPLASH_SCREEN:
      const { [payload.id]: skip, ...nextRefs } = state.splashScreen.refs;
      return {
        ...state,
        splashScreen: { ...state.splashScreen, refs: nextRefs }
      };
    
    default:
      return state;
  }
}

function init({ pathname, menuConfig }) {
  return {
    subheader: { 
      title: '', 
      breadcrumb: [], 
      description: '' 
    },
    splashScreen: { refs: {} }
  };
}

/**
 * Hook para manejar el estado del layout
 */
export const useLayoutState = (pathname, menuConfig) => {
  const [state, dispatch] = useReducer(
    reducer,
    { pathname, menuConfig },
    init
  );

  const actions = useMemo(() => ({
    initializeLayout: (newPathname, newMenuConfig) => {
      dispatch({
        type: actionTypes.INIT,
        payload: { pathname: newPathname, menuConfig: newMenuConfig }
      });
    },
    
    setSubheader: (subheader) => {
      dispatch({
        type: actionTypes.SET_SUBHEADER,
        payload: subheader
      });
    },
    
    showSplashScreen: (id) => {
      dispatch({
        type: actionTypes.SHOW_SPLASH_SCREEN,
        payload: { id }
      });
    },
    
    hideSplashScreen: (id) => {
      dispatch({
        type: actionTypes.HIDE_SPLASH_SCREEN,
        payload: { id }
      });
    }
  }), []);

  const splashScreenVisible = useMemo(
    () => Object.keys(state.splashScreen.refs).length > 0,
    [state.splashScreen.refs]
  );

  // Manejar splash screen
  useEffect(() => {
    if (splashScreenVisible) {
      return () => {
        // Cleanup cuando se desmonta
      };
    }

    const timeout = setTimeout(() => {
      // Ocultar splash screen después de 1 segundo
    }, 1000);

    return () => clearTimeout(timeout);
  }, [splashScreenVisible]);

  return {
    state,
    actions,
    splashScreenVisible
  };
};

/**
 * Provider para el contexto del layout
 */
export const LayoutProvider = ({ children, pathname, menuConfig }) => {
  const { state, actions } = useLayoutState(pathname, menuConfig);

  return (
    <LayoutContext.Provider value={state}>
      <LayoutDispatchContext.Provider value={actions}>
        {children}
      </LayoutDispatchContext.Provider>
    </LayoutContext.Provider>
  );
};

/**
 * Hook para consumir el estado del layout
 */
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

/**
 * Hook para consumir las acciones del layout
 */
export const useLayoutActions = () => {
  const context = useContext(LayoutDispatchContext);
  if (!context) {
    throw new Error('useLayoutActions must be used within a LayoutProvider');
  }
  return context;
};
